import { SharedStateType, SkuBatchInfoDto, SkuInfo } from '../../types'
import { Box, Button, Paper, TextField, Typography } from '@mui/material'
import React, { Fragment, useEffect, useState } from 'react'

import { useApi } from '../../../../hooks/useApi'
import axios from 'axios'
import { ApiConfig } from '../../../../config/api'
import { Status } from '../../../../hooks/useFetchState'
import { Batch } from '../../../../interfaces/Batch'
import { BatchTable } from '../../../../components/_WMS/ListBatch/BatchTable'
import { SkuFefoType } from '../../../../components/_WMS/ListBatch/SkuFefoType'
import { ApiResponse } from '../../../../typings/ApiResponse'
import { Pagination } from '../../../../components/Pagination'
import { usePagination } from '../../../../hooks/usePagination'
import { Loading } from '../../../../components/UI'
import { ProductOrigin } from '../../../../interfaces/Producto/ProductOrigins'

interface ListBatchProps {
  handleCloseModal: () => void
  batchList: Batch[]
  skuList: SkuInfo[]
  positionCode: string
  selectedSku?: SkuBatchInfoDto
  setSharedState: React.Dispatch<React.SetStateAction<SharedStateType>>
  getBackgroundStyle: (
    expirationDate: string | undefined,
    skuProductOrigin: number
  ) => React.CSSProperties
  productOrigins: ProductOrigin[] | undefined
}

export const ListBatch: React.FC<ListBatchProps> = ({
  batchList,
  skuList,
  selectedSku,
  setSharedState,
  handleCloseModal,
  positionCode,
  getBackgroundStyle,
  productOrigins
}) => {
  const [batchToSearch, setBatchToSearch] = useState('')
  const { state, callApi } = useApi<ApiResponse<Batch>>()
  const pagination = usePagination(5)

  const buildFefoType = (): SkuFefoType => {
    const batchNumber = selectedSku?.hasBatch || false
    const expirationDate = selectedSku?.hasExpirationDate || false

    return {
      hasBatchNumber: batchNumber,
      hasExpirationDate: expirationDate,
      modalTitle:
        batchNumber && expirationDate
          ? 'Lotes y Vencimientos'
          : batchNumber
          ? 'Lotes'
          : 'Fechas de Vencimiento',
      modalType:
        batchNumber && expirationDate
          ? 'both'
          : batchNumber
          ? 'batch'
          : 'expiration'
    }
  }

  const skuProductOriginDays =
    productOrigins?.find(
      (el) => el.productOriginId === selectedSku?.productOriginId
    )?.expirationDays || 0

  const [skuFefoType, setSkuFefoType] = useState<SkuFefoType>(() =>
    buildFefoType()
  )

  // Cuando cambie selectedSku puedes recalcular:
  useEffect(() => {
    setSkuFefoType(buildFefoType())
  }, [selectedSku])

  const handleSelectBatch = (batchId: number, pBatchList: Batch[]) => {
    const existingSkuIndex = skuList?.findIndex(
      (item) => item.skuId === selectedSku?.skuId && item.batchId === batchId
    )
    const batch = pBatchList?.find((it) => it.batchId === batchId)

    const updatedSkuList: SkuInfo[] =
      existingSkuIndex !== undefined && existingSkuIndex >= 0
        ? skuList!.map((item, index) =>
            index === existingSkuIndex
              ? {
                  ...item,
                  checkedQty: item.checkedQty + 1,
                  validated: true,
                  skuId: selectedSku?.skuId,
                  batchId: batchId,
                  sku: selectedSku?.sku || '',
                  batch: batch,
                  productOriginid: selectedSku?.productOriginId
                }
              : item
          )
        : [
            ...(skuList || []),
            {
              sku: selectedSku?.sku || '',
              skuId: selectedSku?.skuId,
              checkedQty: 1,
              validated: true,
              batchId: batchId,
              batch: batch,
              productOriginid: selectedSku?.productOriginId
            }
          ]

    setSharedState((prev) => ({
      ...prev,
      skuList: prev.skuList?.filter((item) => item.sku !== selectedSku?.sku)
    }))

    setSharedState((prev) => {
      return { ...prev, skuList: updatedSkuList }
    })

    handleCloseModal()
  }

  const getBatchSku = (
    skuId: number,
    page: number = 0,
    pageSize: number = 5,
    batchNumber?: string
  ) => {
    const url = new URL(`${ApiConfig.InventoryCheckApi}/skuBatch/${skuId}`)

    url.searchParams.set('pageIndex', page.toString())
    url.searchParams.set('pageSize', pageSize.toString())

    if (batchNumber) {
      url.searchParams.set('batchNumber', batchNumber)
    }

    return axios.get(url.toString())
  }

  const handleLoadMoreBatch = (page: number = 0, pageSize: number = 5) => {
    callApi(getBatchSku(selectedSku?.skuId || 0, page, pageSize, batchToSearch))
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    handleLoadMoreBatch(auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  return (
    <Fragment>
      <Typography>{`Lotes en Posición ${positionCode}`}</Typography>
      <BatchTable
        batches={batchList || []}
        showBatchNumber={skuFefoType.hasBatchNumber}
        showExpirationDate={skuFefoType.hasExpirationDate}
        modalType={skuFefoType.modalType}
        onBatchClick={(it) => handleSelectBatch(it.batchId, batchList || [])}
        getBackgroundStyle={getBackgroundStyle}
        skuProductOriginDays={skuProductOriginDays}
      />
      {/* Botón para cargar más */}
      <Box sx={{ textAlign: 'center', margin: '14px' }}>
        <Button variant='contained' onClick={() => handleLoadMoreBatch()}>
          + Más Lotes
        </Button>
      </Box>

      {state.status === Status.Loading ? (
        <Box>
          <Typography>
            <Loading />
          </Typography>
        </Box>
      ) : (
        state.data && (
          <Paper style={{ padding: '10px' }}>
            <TextField
              fullWidth
              variant='outlined'
              placeholder={'Escanear lote...'}
              value={batchToSearch}
              onChange={(e) => setBatchToSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleLoadMoreBatch()
                  setBatchToSearch('')
                }
              }}
            />
            <BatchTable
              batches={state.data?.items || []}
              showBatchNumber={skuFefoType.hasBatchNumber}
              showExpirationDate={skuFefoType.hasExpirationDate}
              modalType={skuFefoType.modalType}
              onBatchClick={(it) =>
                handleSelectBatch(it.batchId, state.data?.items || [])
              }
              getBackgroundStyle={getBackgroundStyle}
              skuProductOriginDays={skuProductOriginDays}
            />
            <Pagination
              pagination={{
                page: pagination.page,
                pageSize: pagination.pageSize,
                totalItem: state.data?.totalItems || 0
              }}
              onPaginationChange={handlePaginationChange}
            />
          </Paper>
        )
      )}
    </Fragment>
  )
}
